/* ==========================================================================
   fk-cart.js — the handwritten-note block in the cart drawer.

   The closed/saved choice is decided by Liquid from `cart.note`, so this file
   owns only the parts a server render cannot: switching into the editing
   state, the character counter, and writing the note back.

   Nothing here caches an element. `cart-drawer.js` replaces the whole of
   `.drawer__inner` on every cart change, so any reference taken now is stale
   after the next add — every handler re-queries from the event target instead,
   and the listeners are delegated from `document` so they survive the swap.
   ========================================================================== */
(function () {
  'use strict';

  var MAX = 200;

  function root(el) { return el ? el.closest('[data-fk-cart-note]') : null; }

  function setState(box, state) {
    if (!box) return;
    box.setAttribute('data-fk-note-state', state);
  }

  function count(box) {
    var field = box.querySelector('[data-fk-note-field]');
    var out = box.querySelector('[data-fk-note-count]');
    var save = box.querySelector('[data-fk-note-save]');
    if (!field) return;
    var n = field.value.length;
    if (out) {
      out.textContent = n + '/' + MAX;
      /* The design turns the counter red near the ceiling — the point is to
         warn before the browser silently stops accepting characters. */
      out.classList.toggle('is-near', n > MAX - 20);
    }
    /* The button keeps its label — Liquid wrote it with the real price — and is
       DISABLED until there is something to add. It used to relabel itself to
       "Skip note" when the field was empty, which meant the row showed two
       controls that both dismissed and none that added. Never rewrite the text
       here: the price must not be hardcoded in JS. */
    if (save) save.disabled = !field.value.trim();
  }

  function announce(box, msg) {
    var s = box.querySelector('[data-fk-note-status]');
    if (s) s.textContent = msg;
  }

  function root_url() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function postJSON(path, body) {
    return fetch(root_url() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(path + ' failed');
      return r.json();
    });
  }

  /* Writes the note and lets Dawn re-render the drawer from the section, so
     the saved state comes back server-rendered rather than assembled here.

     TWO MODES, chosen by Liquid (see fk-cart-note.liquid):
       PAID — data-fk-note-variant is present. The note is a $1 line item and
              its text is the "Note" line item property. Editing means removing
              the old line and adding a new one, because /cart/change.js cannot
              rewrite properties in place. Removing means removing the line.
       FREE — no variant resolved (product deleted or unpublished). Falls back
              to the original cart.note behaviour so the block still works.

     The remove-then-add ordering matters: adding first would leave two note
     lines visible for a moment, and a failed remove would bill twice. */
  function save(box, value) {
    var save_btn = box.querySelector('[data-fk-note-save]');
    if (save_btn) save_btn.disabled = true;

    var variantId = parseInt(box.getAttribute('data-fk-note-variant'), 10);
    var lineKey = box.getAttribute('data-fk-note-line');
    var chain;

    if (variantId) {
      chain = Promise.resolve();
      if (lineKey) {
        chain = chain.then(function () {
          return postJSON('cart/change.js', { id: lineKey, quantity: 0 });
        });
      }
      if (value) {
        chain = chain.then(function () {
          return postJSON('cart/add.js', {
            items: [{ id: variantId, quantity: 1, properties: { Note: value } }]
          });
        });
      }
    } else {
      chain = postJSON('cart/update.js', { note: value });
    }

    return chain
      .then(function () {
        /* Re-render through Dawn's own drawer so the note, the totals and the
           line items can never disagree. The gift meter listens for this too —
           a $1 note changes the cart total and therefore the free-shipping and
           free-gift progress. */
        document.dispatchEvent(new CustomEvent('fk:cart-updated'));
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          return fetch('/cart?sections=cart-drawer,cart-icon-bubble')
            .then(function (r) { return r.json(); })
            .then(function (sections) {
              drawer.renderContents({ sections: sections, sections_html: sections });
            })
            .catch(function () { window.location.reload(); });
        }
        window.location.reload();
      })
      .catch(function () {
        if (save_btn) save_btn.disabled = false;
        announce(box, 'Your note could not be saved. Please try again.');
      });
  }

  document.addEventListener('click', function (e) {
    var open = e.target.closest('[data-fk-note-open]');
    if (open) {
      e.preventDefault();
      var box = root(open);
      setState(box, 'editing');
      count(box);
      var field = box.querySelector('[data-fk-note-field]');
      if (field) { field.focus(); field.setSelectionRange(field.value.length, field.value.length); }
      return;
    }

    var cancel = e.target.closest('[data-fk-note-cancel]');
    if (cancel) {
      e.preventDefault();
      var cbox = root(cancel);
      var cfield = cbox.querySelector('[data-fk-note-field]');
      var quote = cbox.querySelector('[data-fk-note-quote]');
      /* Restore the saved text, so cancelling a half-edit does not leave the
         field holding something the cart never accepted. */
      if (cfield) cfield.value = quote ? quote.textContent.replace(/^“|”$/g, '') : '';
      setState(cbox, cfield && cfield.value.trim() ? 'saved' : 'closed');
      return;
    }

    var savebtn = e.target.closest('[data-fk-note-save]');
    if (savebtn) {
      e.preventDefault();
      var sbox = root(savebtn);
      var sfield = sbox.querySelector('[data-fk-note-field]');
      var val = sfield ? sfield.value.trim() : '';
      announce(sbox, val ? 'Adding your note.' : 'Removing your note.');
      save(sbox, val);
      return;
    }

    var remove = e.target.closest('[data-fk-note-remove]');
    if (remove) {
      e.preventDefault();
      var rbox = root(remove);
      announce(rbox, 'Removing your note.');
      save(rbox, '');
    }
  });

  /* ------------------------------------------------------------------------
     "Complete the gift" — adding an add-on from inside the drawer.

     Routed through Dawn's own renderContents for the same reason the note is:
     the drawer, the totals and the gift meter all have to agree afterwards,
     and there is exactly one code path that guarantees that.
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-fk-addon-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    var id = parseInt(btn.getAttribute('data-fk-addon-add'), 10);
    if (!id) return;
    var box = btn.closest('[data-fk-cart-addons]');
    var status = box ? box.querySelector('[data-fk-addon-status]') : null;
    var label = btn.textContent;

    btn.disabled = true;
    btn.textContent = 'Adding…';
    if (status) status.textContent = 'Adding ' + (btn.getAttribute('data-fk-addon-title') || 'item') + ' to your cart.';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          return fetch('/cart?sections=cart-drawer,cart-icon-bubble')
            .then(function (r) { return r.json(); })
            .then(function (sections) { drawer.renderContents({ sections: sections, sections_html: sections }); });
        }
        window.location.reload();
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = label;
        if (status) status.textContent = 'That could not be added. Please try again.';
      });
  });

  document.addEventListener('input', function (e) {
    if (e.target.matches('[data-fk-note-field]')) count(root(e.target));
  });

  /* Ctrl/Cmd+Enter saves — a 200-character field is small enough that reaching
     for the mouse to commit it is the slow part. */
  document.addEventListener('keydown', function (e) {
    if (!e.target.matches('[data-fk-note-field]')) return;
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      var box = root(e.target);
      save(box, e.target.value.trim());
    }
  });
})();
